import { VIEW_ROLE } from "constants/forge";
import { PATH_API } from "constants/api";
import { ApiResponse } from "interfaces/models/api";
import { ForgeProject } from "interfaces/models/project";
import { axiosECS } from "services/baseAxios";
import { DISABLE_SW_HEADER_KEY } from "constants/serviceWorker";

const pathV1 = PATH_API.FORGE;

export type View = {
  name: string;
  guid: string;
  isMasterView?: boolean;
  role: VIEW_ROLE;
  sheetGuid?: string;
};

interface ForgeReq {
  projectId: string;
  forceGet?: boolean;
}

export const getProjects = async (
  forceGet?: boolean,
  shouldCache?: boolean
): Promise<ApiResponse<ForgeProject[]>> => {
  return axiosECS.get(pathV1, {
    params: { forceGet: forceGet || false, shouldCache },
  });
};

export const getProjectById = async ({
  projectId,
  forceGet,
  shouldCache,
}: ForgeReq & { shouldCache?: boolean }): Promise<
  ApiResponse<ForgeProject>
> => {
  return axiosECS.get(`${pathV1}/${projectId}`, {
    params: { forceGet: forceGet || false, shouldCache },
  });
};

export const getFolderById = async ({
  projectId,
  folderId,
  forceGet,
  shouldCache,
}: ForgeReq & { folderId: string; shouldCache?: boolean }): Promise<
  ApiResponse<any[]>
> => {
  return axiosECS.get(`${pathV1}/${projectId}/folders/${folderId}`, {
    params: { forceGet: forceGet || false, shouldCache },
  });
};

export const getTopFolder = async ({
  projectId,
  forceGet,
  shouldCache,
}: ForgeReq & { shouldCache?: boolean }): Promise<ApiResponse<any[]>> => {
  return axiosECS.get(`${pathV1}/${projectId}/folders/top-folder`, {
    params: { forceGet: forceGet || false, shouldCache },
  });
};

export const getFirstBim = async ({
  projectId,
  shouldCache,
}: ForgeReq & { shouldCache?: boolean }): Promise<ApiResponse<any>> => {
  return axiosECS.get(`${pathV1}/${projectId}/bims/first`, {
    params: { shouldCache },
  });
};

export const getDerivativesByVersionId = async ({
  projectId,
  versionId,
  shouldCache,
}: ForgeReq & { versionId: string; shouldCache?: boolean }): Promise<
  ApiResponse<any>
> => {
  return axiosECS.get(
    `${pathV1}/${projectId}/bims/version/${versionId}/derivatives`,
    {
      params: {
        shouldCache,
      },
    }
  );
};

export const getVersionsByBimFileId = async ({
  projectId,
  bimFileId,
  shouldCache,
}: ForgeReq & { bimFileId: string; shouldCache?: boolean }): Promise<
  ApiResponse<any[]>
> => {
  return axiosECS.get(`${pathV1}/${projectId}/bims/${bimFileId}/versions`, {
    params: {
      shouldCache,
    },
  });
};
export const getExternalIdsByVersionId = async ({
  projectId,
  versionId,
  level,
  shouldCache,
}: ForgeReq & {
  versionId: string;
  level?: string;
  shouldCache?: boolean;
}): Promise<ApiResponse<any>> => {
  return axiosECS.post(`${pathV1}/${projectId}/bims/external-ids`, {
    params: {
      shouldCache,
      versionId,
      level,
    },
  });
};

export const getAECByVersionId = async ({
  projectId,
  versionId,
  shouldCache,
}: ForgeReq & { versionId: string; shouldCache?: boolean }): Promise<
  ApiResponse<any>
> => {
  return axiosECS.get(`${pathV1}/${projectId}/bims/version/${versionId}/aec`, {
    params: { shouldCache },
  });
};

export const getAllBimViewByVersionId = async (
  projectId: string,
  versionId: string,
  shouldCache?: boolean
): Promise<ApiResponse<View[]>> => {
  return axiosECS.get(
    `${pathV1}/${projectId}/bims/version/${versionId}/views`,
    {
      params: { shouldCache },
    }
  );
};

/**
 * Checks if a specific model in a BIM version is empty (contains no objects).
 * @param params Configuration object containing projectId, versionId (bimFileId?version=version) and guid
 * @returns A promise resolving to the API response indicating whether the model is empty
 */
export const checkModelEmpty = async (params: {
  versionId: string;
  projectId: string;
}): Promise<ApiResponse<{ isEmpty: boolean }>> => {
  const { projectId, versionId } = params;

  return axiosECS.get(
    `${pathV1}/${projectId}/bims/version/${versionId}/check-model-empty`,
    {
      headers: {
        [DISABLE_SW_HEADER_KEY]: "true",
      },
    }
  );
};
